import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { TrendingUp, DollarSign, Award, Clock as ClockIcon, MapPin, BarChart3 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import DriverLayout from '../../components/driver/DriverLayout';

const PERIODS = [
  { id: 'daily', label: 'Today' },
  { id: 'weekly', label: 'This Week' },
  { id: 'monthly', label: 'This Month' },
  { id: 'all', label: 'All Time' },
];

const StatCard = ({ icon: Icon, label, value, sub, color = 'pink' }) => (
  <Card className="p-4">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
      <Icon className={`h-4 w-4 text-${color}-500`} />
    </div>
    <div className="text-2xl font-bold text-gray-900">{value}</div>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </Card>
);

const DriverEarnings = () => {
  const [period, setPeriod] = useState('weekly');
  const [summary, setSummary] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const api = (await import('../../utils/api')).default;
      const [s, b] = await Promise.all([
        api.get(`/drivers/me/earnings?period=${period}`),
        api.get(`/drivers/me/earnings/breakdown?period=${period}`),
      ]);
      setSummary(s.data);
      setBreakdown(b.data);
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to load earnings', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatK = (n) => `K${(n || 0).toFixed(2)}`;

  const maxSeriesValue = breakdown?.dailySeries?.length
    ? Math.max(...breakdown.dailySeries.map(d => d.earnings), 1)
    : 1;

  return (
    <DriverLayout>
      <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6" data-testid="driver-earnings-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Earnings Dashboard</h1>
            <p className="text-sm text-gray-500">Track your performance and revenue</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {PERIODS.map(p => (
              <Button
                key={p.id}
                size="sm"
                variant={period === p.id ? 'default' : 'outline'}
                onClick={() => setPeriod(p.id)}
                className={period === p.id ? 'bg-pink-600 hover:bg-pink-700' : ''}
                data-testid={`earnings-period-${p.id}`}
              >
                {p.label}
              </Button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="text-center py-12 text-gray-500">Loading earnings...</div>
        )}

        {!loading && summary && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard icon={DollarSign} label="Net Earnings" value={formatK(summary.netEarnings)} sub={`${summary.totalRides} rides`} color="green" />
              <StatCard icon={TrendingUp} label="Gross" value={formatK(summary.grossEarnings)} sub={`Commission: ${formatK(summary.totalCommission)}`} />
              <StatCard icon={Award} label="Tips" value={formatK(summary.totalTips)} color="yellow" />
              <StatCard icon={MapPin} label="Distance" value={`${summary.totalDistanceKm.toFixed(1)} km`} color="blue" />
            </div>

            {/* Daily series chart */}
            {breakdown?.dailySeries?.length > 0 && (
              <Card className="p-4">
                <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-pink-600" /> Earnings Trend
                </h2>
                <div className="flex items-end gap-1 h-40" data-testid="earnings-chart">
                  {breakdown.dailySeries.map(d => (
                    <div key={d.date} className="flex-1 flex flex-col items-center group">
                      <div className="text-xs text-gray-700 font-medium mb-1 opacity-0 group-hover:opacity-100 transition">
                        K{d.earnings.toFixed(0)}
                      </div>
                      <div
                        className="w-full bg-gradient-to-t from-pink-500 to-pink-300 rounded-t transition-all"
                        style={{ height: `${(d.earnings / maxSeriesValue) * 100}%`, minHeight: d.earnings > 0 ? '4px' : '0' }}
                      />
                      <span className="text-[10px] text-gray-500 mt-1">{d.date.slice(5)}</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              {/* Top routes */}
              <Card className="p-4">
                <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-pink-600" /> Top Routes
                </h2>
                {breakdown?.topRoutes?.length > 0 ? (
                  <div className="space-y-2" data-testid="earnings-top-routes">
                    {breakdown.topRoutes.map((r, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{r.pickup} → {r.dropoff}</p>
                          <p className="text-xs text-gray-500">{r.rides} rides</p>
                        </div>
                        <span className="text-sm font-bold text-pink-600">{formatK(r.earnings)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No completed rides in this period.</p>
                )}
              </Card>

              {/* By hour */}
              <Card className="p-4">
                <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <ClockIcon className="h-5 w-5 text-pink-600" /> Peak Hours
                </h2>
                {breakdown?.byHourOfDay?.length > 0 ? (
                  <div className="space-y-2" data-testid="earnings-by-hour">
                    {breakdown.byHourOfDay.slice(0, 5).sort((a, b) => b.earnings - a.earnings).map(h => (
                      <div key={h.hour} className="flex items-center gap-3">
                        <span className="text-xs text-gray-500 w-12">{String(h.hour).padStart(2, '0')}:00</span>
                        <div className="flex-1 bg-gray-100 rounded-full h-2">
                          <div className="bg-pink-500 h-2 rounded-full" style={{ width: `${Math.min(100, (h.earnings / Math.max(...breakdown.byHourOfDay.map(x => x.earnings), 1)) * 100)}%` }} />
                        </div>
                        <span className="text-sm font-medium text-gray-900 w-20 text-right">{formatK(h.earnings)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No data for this period.</p>
                )}
              </Card>
            </div>
          </>
        )}
      </div>
    </DriverLayout>
  );
};

export default DriverEarnings;
